import React, { useEffect, useRef } from "react"
import { View, StyleSheet, Animated, Easing } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"

/**
 * Signal de rareté discret d'une carte de session : « X places restantes » +
 * une fine jauge. Le chiffre et le remplissage viennent de la formule
 * DÉTERMINISTE partagée avec la LP ({@see stageSessionScarcity}) — ce composant
 * ne fait que RENDRE, aucun calcul de rareté ici.
 *
 * La jauge se remplit à l'apparition (léger ressort d'entrée) jusqu'à
 * `gaugeFill` (0..1) : plus la session est prisée/proche, plus elle est pleine.
 * Sous un seuil de places, la teinte passe à l'orange d'alerte pour appuyer le
 * FOMO sans crier.
 *
 * `compact` : le chiffre seul, sans jauge — dans un écran déjà dense (le
 * compositeur), la barre est du décor.
 *
 * `full` (bool) court-circuite la rareté : la semaine est entrée dans son délai
 * plancher (elle démarre bientôt) → état distinct « Complet », sans jauge ni
 * décompte de places (elle n'est plus réservable).
 */
const SCARCE_THRESHOLD = 10

const StageScarcityMeter = ({
    places,
    gaugeFill,
    full = false,
    compact = false,
}) => {
    const target = Math.max(0, Math.min(1, gaugeFill || 0))
    const fill = useRef(new Animated.Value(0)).current

    useEffect(() => {
        Animated.timing(fill, {
            toValue: target,
            duration: 620,
            easing: Easing.out(Easing.cubic),
            // Largeur en % → piloté par le layout, pas le driver natif.
            useNativeDriver: false,
        }).start()
    }, [target, fill])

    // Semaine complète : état terminal « Complet », ni jauge ni décompte.
    if (full) {
        return (
            <View style={styles.completeRow}>
                <FontAwesome5
                    name="lock"
                    solid
                    size={10}
                    color={theme.colors.grey}
                />
                <TextM
                    fontWeight="Bold"
                    fontSize={11}
                    color={theme.colors.grey}
                >
                    Complet
                </TextM>
            </View>
        )
    }

    const scarce = places <= SCARCE_THRESHOLD
    const accent = scarce ? theme.colors.warning : theme.colors.primaryLight
    const plural = places > 1 ? "s" : ""

    return (
        <View style={styles.wrap}>
            <View style={styles.labelRow}>
                <FontAwesome5 name="fire-alt" solid size={10} color={accent} />
                <TextM
                    fontWeight="Bold"
                    fontSize={11}
                    color={scarce ? theme.colors.warning : theme.colors.grey}
                >
                    {`${places} place${plural} restante${plural}`}
                </TextM>
            </View>
            {compact ? null : (
                <View style={styles.track}>
                    <Animated.View
                        style={[
                            styles.fill,
                            {
                                backgroundColor: accent,
                                width: fill.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: ["0%", "100%"],
                                }),
                            },
                        ]}
                    />
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    wrap: { marginTop: 6, gap: 4 },
    labelRow: { flexDirection: "row", alignItems: "center", gap: 5 },
    completeRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        marginTop: 6,
    },
    track: {
        height: 5,
        borderRadius: 3,
        backgroundColor: theme.colors.lightMore,
        overflow: "hidden",
    },
    fill: { height: "100%", borderRadius: 3 },
})

export default StageScarcityMeter
